import { useEffect, useState } from 'react'
import {
  INTERNAL_NOTIFY_ACTION_OPTIONS,
  INTERNAL_NOTIFY_TEAM_OPTIONS,
  NOTES_NOTIFY_DIALOG_BUTTON_CLASS,
} from '../../constants/dialogs'
import { showValidationMessage } from '@/lib/utils/notifications'
import type { InternalNotesNotifySelection } from '../../utils/internalNotesSubmit'
import { isInternalNotesNotifySelectionValid } from '../../utils/internalNotesSubmit'
import { ResidentDialogShell } from './ResidentDialogShell'
import type { InternalNotesNotifyDialogProps, InternalNotifyOption } from '../../types'
import { cn } from '@/lib/utils/helpers'
import styles from './InternalNotesNotifyDialog.module.css'

/**
 * Angular `StatementPopupComponent` with `emailtype === 'notemessage'` and `type === 'internal'`.
 */
export function InternalNotesNotifyDialog({
  isOpen,
  processing = false,
  onClose,
  onSubmit,
}: InternalNotesNotifyDialogProps) {
  const [emailSendTo, setEmailSendTo] = useState('')

  useEffect(() => {
    if (!isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the form selection each time the dialog opens
    setEmailSendTo('')
  }, [isOpen])

  const handleSubmit = () => {
    const selection: InternalNotesNotifySelection = { email_sendto: emailSendTo }
    if (!isInternalNotesNotifySelectionValid(selection)) {
      showValidationMessage('Please select an option!')
      return
    }
    onSubmit(selection)
  }

  const renderOption = (option: InternalNotifyOption) => {
    const checked = emailSendTo === option.value
    return (
      <label
        key={option.value}
        className={cn(styles.optionCard, checked && styles.optionCardSelected)}
        data-testid={`internal-notes-notify-option-${option.value}`}
      >
        <input
          type="radio"
          name="internal-notes-email-sendto"
          className={styles.optionRadio}
          checked={checked}
          onChange={() => setEmailSendTo(option.value)}
        />
        <span className={styles.optionContent}>
          <span className={styles.optionTitle}>{option.label}</span>
          {option.subtitle ? <span className={styles.optionSubtitle}>{option.subtitle}</span> : null}
        </span>
      </label>
    )
  }

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      title="Notes & Messages"
      headerVariant="centeredTitle"
      onClose={onClose}
      panelDataTestId="internal-notes-notify-dialog-panel"
      footer={
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            disabled={processing}
            onClick={handleSubmit}
            data-testid="internal-notes-notify-dialog-submit-button"
            className={NOTES_NOTIFY_DIALOG_BUTTON_CLASS}
          >
            {processing ? 'Please wait…' : 'Submit'}
          </button>
        </div>
      }
    >
      <div className={styles.body} data-testid="internal-notes-notify-dialog-body">
        <p className={styles.headerTitle}>Who should be notified?</p>
        <p className={styles.headerSubtitle}>Select one notification option or action.</p>

        <div className={styles.section}>
          <div className={styles.sectionLabelRow}>
            <span className={styles.sectionLabel}>Notify teams</span>
            <span className={styles.sectionHint}>Choose one</span>
          </div>
          <div className={styles.optionsGrid}>{INTERNAL_NOTIFY_TEAM_OPTIONS.map(renderOption)}</div>
        </div>

        <div className={styles.orDivider} aria-hidden="true">
          <span className={styles.orDividerLine} />
          <span className={styles.orDividerText}>OR</span>
          <span className={styles.orDividerLine} />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionLabelRow}>
            <span className={styles.sectionLabel}>Other actions</span>
            <span className={styles.sectionHint}>Choose one</span>
          </div>
          <div className={styles.optionsGrid}>{INTERNAL_NOTIFY_ACTION_OPTIONS.map(renderOption)}</div>
        </div>

        <p className={styles.footerNote}>Only one option can be selected.</p>
      </div>
    </ResidentDialogShell>
  )
}
